import {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';

import cn from 'classnames/bind';
import { useToggleState } from 'hooks';

import s from './Dropdown.module.scss';

const Context = createContext({});
const cx = cn.bind(s);

const TOP_TRANSITION_VALUE = 20;

const Dropdown = forwardRef((
  {
    children, className, triggerType = 'hover', align, initialIsOpen = false, ...attrs
  },
  ref,
) => {
  const rootRef = useRef(null);
  const {
    isActive: isOpen, toggle, open, close,
  } = useToggleState(initialIsOpen);
  useImperativeHandle(ref, () => ({
    close,
  }));
  const eventProps = {};
  const headerEvents = {};
  if (triggerType === 'click') {
    headerEvents.onClick = toggle;
  } else if (triggerType === 'hover') {
    headerEvents.onMouseEnter = open;
    eventProps.onMouseLeave = close;
  }
  useEffect(() => {
    const onWindowsClick = (ev) => {
      if (
        rootRef.current
        && (rootRef.current === ev.target || rootRef.current.contains(ev.target))
      ) {
        return;
      }
      close();
    };

    if (isOpen) {
      window.addEventListener('click', onWindowsClick);
    } else {
      window.removeEventListener('click', onWindowsClick);
    }
    return () => {
      window.removeEventListener('click', onWindowsClick);
    };
  }, [isOpen, close]);
  return (
    <Context.Provider
      value={{
        isOpen,
        align,
        open,
        close,
        toggle,
        headerEvents,
      }}
    >
      <div className={cx(className, 'dropdown')} {...attrs} {...eventProps} ref={rootRef}>
        {children}
      </div>
    </Context.Provider>
  );
});
const DropdownHeader = ({ children, className, ...attrs }) => {
  const { isOpen, headerEvents } = useContext(Context);

  return (
    <button className={cx('dropdownHeader')} {...attrs} {...headerEvents}>
      {typeof children === 'function' ? children({ isOpen }) : children}
    </button>
  );
};
Dropdown.Header = DropdownHeader;

const DropdownContent = ({ children, className, ...attrs }) => {
  const ref = useRef(null);
  const [maxHeight, setMaxHeight] = useState(null);
  const { isOpen, align } = useContext(Context);
  const calcMaxHeight = () => {
    if (!ref.current) {
      return;
    }
    const offsetTop = ref.current.getBoundingClientRect().top + TOP_TRANSITION_VALUE;
    setMaxHeight(window.innerHeight - offsetTop);
  };
  useEffect(() => {
    if (isOpen) {
      calcMaxHeight();
      window.addEventListener('resize', calcMaxHeight);
    } else {
      window.removeEventListener('resize', calcMaxHeight);
    }
    return () => {
      window.removeEventListener('resize', calcMaxHeight);
    };
  }, [isOpen]);
  return (
    <div
      className={cx('dropdownContentContainer', className, {
        dropdownActive: isOpen,
        dropdownAlignCenter: align === 'center',
        dropdownAlignEnd: align === 'end',
      })}
      {...attrs}
    >
      <div className={cx('dropdownContentAnimationContainer')}>
        <div className={cx('dropdownContentContainerTriangle')}>
          <div className={cx('dropdownContentContainerTriangleBorder')}></div>
        </div>
        <div
          ref={ref}
          style={{ maxHeight: maxHeight ? `${maxHeight}px` : null }}
          className={cx('dropdownContent')}
        >
          {typeof children === 'function' ? children({ isOpen }) : children}
        </div>
      </div>
    </div>
  );
};

Dropdown.Content = DropdownContent;
Dropdown.displayName = 'Dropdown';
export default Dropdown;
